import React from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Container,
  Card,
  CardContent,
  Typography,
  IconButton,
  Divider,
  Grid,
  Tooltip,
  Chip,
  alpha,
  Box,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import Header from './Header';
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function ReleasePlanViewer() {
  interface Release {
    name: string;
    repo_link: string;
    tag: string;
    description: string;
    note: string;
    release_document: string;
  }

  interface ReleaseData {
    name: string;
    version: string;
    description: string;
    releases: Release[];
  }

  const [releaseData, setReleaseData] = React.useState<ReleaseData>({
    name: '',
    version: '',
    description: '',
    releases: [],
  });
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const [fileUploaded, setFileUploaded] = React.useState<boolean>(false);
  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      const data = event.target.result;
      console.log(data);
      setReleaseData(JSON.parse(data));
      setFileUploaded(true);
    };
    reader.readAsText(file);
  };

  const fileUplaodScreen = () => {
    return (
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
        <Button component="label" role={undefined} variant="contained" tabIndex={-1} startIcon={<CloudUploadIcon />}>
          Upload Release Manifest
          <VisuallyHiddenInput type="file" onChange={(event: any) => readFile(event.target.files[0])} multiple />
        </Button>
      </Container>
    );
  };

  const plannerContent = () => {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingTop: '20px' }}>
          <AssignmentIcon color="primary" fontSize="large" />
          <Tooltip title="Release Name" placement="top">
            <Typography variant="h4">{releaseData.name}</Typography>
          </Tooltip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <NewReleasesIcon color="secondary" />
          <Tooltip title="Release Version" placement="top">
            <Typography variant="h6" color="textSecondary">
              Version: {releaseData.version}
            </Typography>
          </Tooltip>
        </div>
        <br />
        <Typography variant="h6" gutterBottom>
          Releases ({releaseData.releases.length})
        </Typography>
        {releaseData.releases &&
          releaseData.releases.map((release, index) => (
            <Card key={index} variant="outlined" style={{ marginBottom: '5px', padding: '5px' }}>
              <CardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                  <Typography variant="h6">{release.name}</Typography>
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: 'right' }}>
                    Tag: {release.tag}
                  </Typography>
                </div>

                <Divider />
                <div style={{ padding: '10px' }}>
                  <Tooltip title="Release Note" placement="top">
                    <Typography variant="body2" color="textSecondary">
                      {' '}
                      {release.note}
                    </Typography>
                  </Tooltip>
                  <Divider />
                  <Tooltip title="Release Description" placement="top">
                  <Typography variant="body1">{release.description}</Typography>
                  </Tooltip>
                </div>
                <Divider />
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                  {release.repo_link ? (
                    <IconButton href={release.repo_link} target="_blank" rel="noopener" color="primary">
                      <GitHubIcon />
                    </IconButton>
                  ) : (
                    <> </>
                  )}
                  {release.release_document ? (
                    <IconButton href={release.release_document} target="_blank" rel="noopener" color="secondary">
                      <DescriptionIcon />
                    </IconButton>
                  ) : (
                    <> </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
      </>
    );
  };

  return (
    <>
      <Header>
        {' '}
        <Grid sx={{ paddingTop: 2, alignContent: 'center' }}>
          <center>
            <Button variant="contained" sx={{ marginLeft: 2 }} href={'/release-plan-editor'}>
              Editor
            </Button>
          </center>
        </Grid>
        <Grid sx={{ paddingTop: 2, alignContent: 'center' }}>{!fileUploaded ? fileUplaodScreen() : plannerContent()}</Grid>
        
        {/* Give Back Section */}
        <Card
          sx={{
            mt: 4,
            mb: 2,
            mx: 2,
            background: `linear-gradient(135deg, ${alpha('#FFD700', 0.1)}, ${alpha('#2196f3', 0.1)})`,
            border: `1px solid ${alpha('#FFD700', 0.3)}`,
            borderRadius: '15px',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: `linear-gradient(90deg, #FFD700, #2196f3, #FFD700)`,
            },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, #FFD700, #2196f3)`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                💛 Donate Back – Not Just Money
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                If Release Pilot boosted your productivity by 0.1%—give back through <strong>impact, not money</strong> 🌟
              </Typography>
            </Box>

            <Box 
              sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              {['🤝 Help others', '👥 Mentor fellow developer', '🚀 Share opportunities', '💡 Contribute skills', '❤️ Spread kindness'].map((item, index) => (
                <Chip
                  key={index}
                  label={item}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: alpha('#FFD700', 0.5),
                    color: 'text.primary',
                    '&:hover': {
                      backgroundColor: alpha('#FFD700', 0.1),
                      transform: 'scale(1.02)',
                    },
                  }}
                />
              ))}
            </Box>

            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              Every small action matters. Let your gratitude become someone else's breakthrough. 🙌
            </Typography>
          </CardContent>
        </Card>

        {/* Crafted with Love */}
        <Typography 
          variant="body1" 
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            mt: 3,
            mb: 2,
            mx: 2,
            fontSize: '1.1rem',
          }}
        >
          Crafted with ❤️ in India
        </Typography>
      </Header>
    </>
  );
}
